import {
  EditIcon,
  DeleteIcon,
  MultipleContactModal,
} from '@/components/_common';
import { ContactType } from '@/core/domain/_shared/ContactType';
import { ContactDetailList } from '@/core/domain/people/_shared/Contact';
import {
  useCreateCustomerContact,
  useUpdateCustomerContact,
} from '@/core/domain/people/CustomerContacts/CustomerContacts.hooks';
import { CustomerContact } from '@/core/domain/people/CustomerContacts/CustomerContacts.types';
import { PersonType } from '@/core/domain/people/Customers/Customers.types';
import { useDisclosure } from '@/hooks';
import { useContactColumns } from '@/hooks/useContactColumns';
import { isAppPortocar } from '@/index';
import { removeMaskDetailList } from '@/utils/mask/removeMaskDetailList';
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  message,
  Modal,
  Row,
  Space,
  Table,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useState } from 'react';
import { useIntl } from 'react-intl';

interface Props {
  contacts?: CustomerContact[];
  setContacts?: React.Dispatch<React.SetStateAction<CustomerContact[]>>;
  personType: PersonType;
  hasError: boolean;
  clearError: VoidFunction;
}

export default function CustomerGeneralContactsCreate({
  contacts,
  setContacts,
  personType,
  hasError,
  clearError,
}: Props) {
  const intl = useIntl();

  const [form] = Form.useForm();

  const [updated, setUpdated] = useState(Number(new Date()));

  const updateContactMutation = useUpdateCustomerContact();
  const createContactMutation = useCreateCustomerContact();

  const { columns: columnsContact } = useContactColumns<CustomerContact>();

  const [contactId, setContactId] = useState<number>();

  const [viewContact, viewContactAction] = useDisclosure(false, {
    onClose: () => {
      setContactId(undefined);
      form.resetFields();
    },
  });

  const handleRemoveClick = (contact: CustomerContact) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'customers.contact.remove',
      }),
      content: `${intl.formatMessage({
        id: 'customers.contact.remove.alert',
      })} ${contact.name}`,
      onOk: async () => {
        if (contacts?.length === 1 && isAppPortocar) {
          message.destroy();
          message.warning(
            'O cadastro requer pelo menos um item de contatos obrigatório.',
          );
        } else if (setContacts) {
          setContacts((prev) =>
            prev.filter((prevContact) => prevContact.id !== contact.id),
          );
        }
      },
      okType: 'danger',
    });
  };

  async function handleSave() {
    try {
      if (!setContacts || !contacts) return;

      const values = await form.validateFields();

      const detailList = removeMaskDetailList(values.detailList);

      if (
        isAppPortocar &&
        (![ContactType.Email].some((type) =>
          detailList.some((item) => item.contactType === type),
        ) ||
          ![ContactType.Mobile, ContactType.Phone, ContactType.WhatsApp].some(
            (type) => detailList.some((item) => item.contactType === type),
          ))
      ) {
        message.destroy();
        message.warning(
          'O cadastro requer pelo menos um e-mail e um tipo de telefone para contato.',
        );
        return;
      }

      const indexContactWithSameName = contacts?.findIndex(
        (contact) => contact.id === contactId,
      );

      if (indexContactWithSameName >= 0) {
        setContacts((prev) => {
          prev[indexContactWithSameName] = {
            ...prev[indexContactWithSameName],
            ...values,
            detailList: values.detailList?.map(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (detail: any, index: number) => ({
                ...detail,
                id: Number(`${index}${Number(new Date())}`),
              }),
            ),
          };
          return prev;
        });
      } else {
        const itemId = Number(new Date());
        setContacts((prev) => [
          ...prev,
          {
            ...values,
            id: itemId,
            detailList: values.detailList?.map(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (detail: any, index: number) => ({
                ...detail,
                id: Number(`${index}${Number(new Date())}`),
              }),
            ),
          },
        ]);
      }

      viewContactAction.close();
      form.resetFields();
      setUpdated(Number(new Date()));
      clearError();
    } catch {}
  }

  async function handleRemoveContactDetail(detailId: number) {
    const formValues = form.getFieldsValue();

    const detailList =
      (formValues.detailList as ContactDetailList[]) || undefined;

    const contactType = detailList?.find(
      (detail) => detail.id === detailId,
    )?.contactType;

    const isEmail = contactType === ContactType.Email;

    const emails = detailList.filter(
      (detail) =>
        detail.id !== detailId && detail.contactType === ContactType.Email,
    );

    const phones = detailList.filter(
      (detail) =>
        detail.id !== detailId &&
        (detail.contactType === ContactType.Mobile ||
          detail.contactType === ContactType.Phone ||
          detail.contactType === ContactType.WhatsApp),
    );

    if (isEmail ? emails.length === 0 : phones.length === 0) {
      message.destroy();
      message.warning(
        `O cadastro requer pelo menos um ${
          contactType === ContactType.Email ? 'e-mail' : 'telefone'
        } obrigatório.`,
      );
      return;
    }

    if (setContacts)
      setContacts((prev) =>
        prev.map((prevContact) => {
          if (prevContact.id === contactId) {
            const result = {
              ...prevContact,
              detailList: formValues.detailList.filter(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (detail: any) => detail.id !== detailId,
              ),
            };

            form.setFieldsValue(result);

            return result;
          }
          return prevContact;
        }),
      );
  }

  const columns: ColumnsType<CustomerContact> = [
    ...columnsContact,
    {
      key: 'action',
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      align: 'center',
      fixed: 'right',
      width: 80,
      render: (_, row) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => {
                form.setFieldsValue(row);
                setContactId(row.id);
                viewContactAction.open();
              },
            }}
          />

          <Divider type="vertical" />

          <DeleteIcon onClick={() => handleRemoveClick(row)} />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        style={{
          borderRadius: 10,
          borderColor: hasError ? 'red' : undefined,
        }}
      >
        <Row style={{ marginBottom: 24 }}>
          <Col flex={1}>
            <Typography.Title level={4}>Contatos</Typography.Title>
          </Col>
          <Col>
            <Button type="primary" onClick={viewContactAction.open}>
              Adicionar contato
            </Button>
          </Col>
        </Row>

        <Table
          key={updated}
          pagination={false}
          columns={columns}
          dataSource={contacts}
          scroll={{ x: true }}
        />
      </Card>

      <MultipleContactModal
        open={viewContact}
        onCancel={viewContactAction.close}
        onOk={handleSave}
        confirmLoading={
          createContactMutation.isLoading || updateContactMutation.isLoading
        }
        form={form}
        personType={
          personType === PersonType.Natural ? 'NaturalPerson' : 'LegalPerson'
        }
        onDetailRemove={handleRemoveContactDetail}
      />
    </>
  );
}
